import { DEFAULT_SEARCH_LIST_SIZE } from '@modules-rocco/filters/config/filter-url.config';
import {
    type AggregationSearchableListResults,
    isSearchListItem,
    isSearchListItemWithUrl,
    type SearchListItem,
    type SearchListItemWithUrl,
} from '@modules-rocco/search/types/aggregations';
import {
    createSingleValueFilterState,
    type SingleValueFilterState,
} from '@modules-rocco/filters/filter-state/single-value-filter-state';
import type { MultipleValuesFilterState } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import type { FilterGroupOptions } from '@rocco/types/filter-group-options';

export interface SearchableListFilterState<T = string>
    extends MultipleValuesFilterState<T> {
    search: string;
    preserveSort: boolean;
    size: number;
    hasMoreItems: boolean;
    after: Record<string, unknown> | null;
    firstCharacterGroups: Partial<Record<keyof FilterGroupOptions, number>>;
}

export const createSearchableListFilterState = (
    params: PartialProps<
        SearchableListFilterState,
        | 'allowMultipleSelectedItems'
        | 'preserveSort'
        | 'size'
        | 'hasMoreItems'
        | 'after'
    >
): SearchableListFilterState => {
    const size = params.size ?? DEFAULT_SEARCH_LIST_SIZE;
    const preserveSort = params.preserveSort ?? true;
    const hasMoreItems = !size || params.items.length >= size;
    const after = params.after ?? null;

    let items = [...params.items];

    if (!preserveSort) {
        items = items.sort((a, b) =>
            (a.label ?? '') > (b.label ?? '') ? 1 : -1
        );
    }

    return {
        ...params,
        items: [...items],
        selectedItems: [...params.selectedItems],
        search: params.search,
        preserveSort,
        size,
        hasMoreItems,
        after,
        firstCharacterGroups: params.firstCharacterGroups,
        allowMultipleSelectedItems:
            params.allowMultipleSelectedItems !== undefined
                ? params.allowMultipleSelectedItems
                : true,
    };
};

export const createSearchableListFilterStateFromAggregationResult = (
    aggregationResult?: AggregationSearchableListResults,
    search?: string,
    allowMultipleSelectedItems: boolean = true
): SearchableListFilterState => {
    const getLabel = (item: string | SearchListItem | SearchListItemWithUrl) =>
        isSearchListItem(item)
            ? item.label
            : isSearchListItemWithUrl(item)
              ? item.Title
              : item;

    const getValue = (item: string | SearchListItem | SearchListItemWithUrl) =>
        isSearchListItem(item)
            ? item.value
            : isSearchListItemWithUrl(item)
              ? item.URLSegment
              : item;

    // Create a Set from selectedItems for efficient lookup.
    const selectedItemValues = new Set(
        aggregationResult?.selectedItems?.map((item) => getValue(item)) ?? []
    );

    // collect character groups (letters / numbers / other)
    type GroupCharactersInput = Record<string, number>;
    type GroupCharactersResult = Partial<
        Record<keyof FilterGroupOptions, number>
    >;

    const groupCharacters = (
        input: GroupCharactersInput
    ): GroupCharactersResult => {
        const result: GroupCharactersResult = {};

        for (const [key, value] of Object.entries(input)) {
            if (value > 0) {
                let group: keyof FilterGroupOptions;

                if (/^[A-Z]$/i.test(key)) {
                    group = key.toUpperCase() as keyof FilterGroupOptions;
                } else if (/^\d$/.test(key)) {
                    group = 'numeric';
                } else {
                    group = 'other';
                }

                const groupValue = result[group];
                result[group] = groupValue ? groupValue + value : value;
            }
        }

        return result;
    };

    return createSearchableListFilterState({
        items:
            aggregationResult?.items?.map((item) =>
                createSingleValueFilterState({
                    label: getLabel(item),
                    value: getValue(item),
                    checked: selectedItemValues.has(getValue(item)),
                })
            ) ?? [],
        selectedItems:
            aggregationResult?.selectedItems?.map((item) =>
                createSingleValueFilterState({
                    label: getLabel(item),
                    value: getValue(item),
                    checked: true,
                })
            ) ?? [],
        after: aggregationResult?.after,
        search: search ?? '',
        firstCharacterGroups: groupCharacters(
            aggregationResult?.firstCharacterGroups ?? {}
        ),
        allowMultipleSelectedItems,
    });
};

/**
 * Toggles the checked state of a specific item in a SearchableListFilterState.
 * If the item exists in the state, it updates its checked status and adjusts the selectedItems accordingly.
 * If the item doesn't exist, it returns the original state unchanged.
 *
 * @param state - The current SearchableListFilterState
 * @param item - The SingleValueFilterState item to toggle
 * @param checked - The new checked state for the item
 * @returns A new SearchableListFilterState with the updated item and selectedItems
 */
export const toggleSearchableListFilterStateItem = (
    state: SearchableListFilterState,
    item: SingleValueFilterState,
    checked: boolean
): SearchableListFilterState => {
    const index = state.items.findIndex((i) => i.value === item.value);

    if (index > -1) {
        state.items[index] = { ...item, checked };
    }

    return createSearchableListFilterState({
        ...state,
        selectedItems:
            checked && index > -1
                ? [...state.selectedItems, { ...item, checked: true }]
                : state.selectedItems.filter((i) => i.value !== item.value),
    });
};
